import { select } from 'd3-selection'
import { buildSingleLayerMap } from '../../core/stat-map'
import { registerLayerType } from '../../core/layer-registry'
import * as TrivariateLegend from '../../legend/choropleth/legend-choropleth-trivariate'
import { getRegionsSelector, executeForAllInsets, spaceAsThousandSeparator } from '../../core/utils'
import { tricolore, tricoloreSextant, CompositionUtils } from '../../lib/tricolore/src'
//types
/** @typedef {import('../../types/core/MapInstance').MapInstance} MapInstance */
/** @typedef {import('../../types/layers/choropleth/TrivariateChoroplethConfig').TrivariateChoroplethConfig} TrivariateChoroplethConfig */
/** @typedef {import('../../types/layers/choropleth/TrivariateChoroplethMap').TrivariateChoroplethMap} TrivariateChoroplethMap */

/**
 * Trivariate (ternary) choropleth map — Observable-style
 *
 * @param {TrivariateChoroplethConfig} [config]
 * @returns {TrivariateChoroplethMap}
 */
export const map = function (config) {
    return buildSingleLayerMap('trivariateChoropleth', config)
}

export const decorateTrivariateChoroplethLayer = function (out, config) {

    // ===============================
    // Configuration
    // ===============================

    out.ternaryCodes_ = ['v1', 'v2', 'v3']

    out.noDataFillStyle_ = '#ADADAD'

    // tricolore parameters
    out.ternarySettings_ = {
        hue: 20,
        chroma: 120,
        lightness: 70,
        contrast: 1,
        spread: 0.8,
        breaks: 5,
        meanCentering: true,
        sextant: false,
        sextantColors: ['#FFFF00', '#B3DCC3', '#01A0C6', '#B8B3D8', '#F11D8C', '#FFB3B3'],
    }

    // internal cache (index → color)
    out._ternaryColors_ = null
    out._ternaryData_ = null //stats composition data
    out._ternaryCenter_ = null

    // ===============================

    // Tooltip renderer
    out.tooltip_.textFunction = tooltipTextFunctionTrivariate

    out.getTernaryStatCodes = function () {
        return out.getEncodingStats?.('color', out.ternaryCodes_) || out.ternaryCodes_
    }

    // ===============================
    // Getters / setters
    // ===============================
    const paramNames = ['ternaryCodes_', 'noDataFillStyle_', 'ternarySettings_']
    paramNames.forEach((att) => {
        const name = att.slice(0, -1)

        out[name] = function (v) {
            if (!arguments.length) return out[att]

            if (att === 'ternarySettings_') {
                // MERGE, don’t replace
                out.ternarySettings_ = {
                    ...out.ternarySettings_,
                    ...v,
                }
            } else {
                out[att] = v
            }

            return out
        }
    })

    //override attribute values with config values
    if (config) {
        paramNames.forEach(function (key) {
            let k = key.slice(0, -1) // remove trailing underscore
            if (config[k] != undefined) out[k](config[k])
        })
    }

    // ===============================
    // Classification (compute colors ONCE)
    // ===============================
    //@override
    out.updateClassification = function () {
        applyClassificationToMap(out)
        return out
    }

    function applyClassificationToMap(out) {
        out._ternaryData_ = []
        out._ternaryColorById_ = new Map()
        out._ternaryClassById_ = new Map()
        out._ternaryNoDataIds_ = new Set()
        out._ternaryCenter_ = null

        const flattenRegionFeatures = (features) => {
            if (!Array.isArray(features) || features.length === 0) return []
            return Array.isArray(features[0]) ? features.flat() : features
        }

        const [c1, c2, c3] = out.getTernaryStatCodes()
        const statData1 = out.statData(c1)
        const statData2 = out.statData(c2)
        const statData3 = out.statData(c3)

        // collect features from main map AND all insets
        const allMaps = [out]
        if (out.insetTemplates_) {
            executeForAllInsets(out.insetTemplates_, out.svgId_, (inset) => allMaps.push(inset))
        }

        const filtered = []
        const seenIds = new Set()

        allMaps.forEach((map) => {
            const regionFeatures = flattenRegionFeatures(map.Geometries.getRegionFeatures())
            // For nutsLevel 'mixed', getRegionFeatures() only returns the mixed NUTS 0-3 levels -
            // it excludes the raw cntrg (background country) layer, even though those regions are
            // still rendered and styled (see getRegionsSelector, which always includes #em-cntrg
            // path). Without this, background-only countries with no NUTS0-equivalent entry (e.g.
            // Moldova, Georgia) never get classified and always fall back to the CSS background
            // fill, no matter what their stat value is.
            const cntrgFeatures = map.Geometries.geoJSONs?.cntrg || []
            const features = regionFeatures.concat(cntrgFeatures)
            if (!features || features.length === 0) return
            features.forEach((f) => {
                const id = f?.properties?.id
                if (!id) return
                if (seenIds.has(id)) return
                seenIds.add(id)
                const sv1 = statData1.get(id)
                const sv2 = statData2.get(id)
                const sv3 = statData3.get(id)
                const v1 = +sv1?.value
                const v2 = +sv2?.value
                const v3 = +sv3?.value
                if (Number.isFinite(v1) && Number.isFinite(v2) && Number.isFinite(v3) && v1 + v2 + v3 > 0) {
                    filtered.push({ id, values: [v1, v2, v3] })
                } else if (sv1 || sv2 || sv3) {
                    // region is part of the dataset (e.g. value ':') but has no usable composition -
                    // flag it explicitly so it's styled as "no data" rather than left as background
                    out._ternaryNoDataIds_.add(id)
                }
            })
        })

        out._ternaryData_ = filtered.map((d) => d.values)
        out._ternaryCenter_ = out.ternarySettings_.meanCentering && out._ternaryData_.length > 0 ? CompositionUtils.centre(out._ternaryData_) : [1 / 3, 1 / 3, 1 / 3]

        const colors = out.ternarySettings_.sextant
            ? tricoloreSextant(out._ternaryData_, {
                  center: out._ternaryCenter_,
                  values: out.ternarySettings_.sextantColors,
              })
            : tricolore(out._ternaryData_, {
                  center: out._ternaryCenter_,
                  breaks: out.ternarySettings_.breaks,
                  hue: out.ternarySettings_.hue,
                  chroma: out.ternarySettings_.chroma,
                  lightness: out.ternarySettings_.lightness,
                  contrast: out.ternarySettings_.contrast,
                  spread: out.ternarySettings_.spread,
              })

        filtered.forEach((d, i) => {
            const color = colors[i]
            if (color != null && !color.includes('NaN')) {
                out._ternaryColorById_.set(d.id, color)
                out._ternaryClassById_.set(d.id, i)
            }
        })

        // share lookup with all insets
        if (out.insetTemplates_) {
            executeForAllInsets(out.insetTemplates_, out.svgId_, (inset) => {
                inset._ternaryColorById_ = out._ternaryColorById_
                inset._ternaryClassById_ = out._ternaryClassById_
                inset._ternaryNoDataIds_ = out._ternaryNoDataIds_
                inset._ternaryCenter_ = out._ternaryCenter_
                inset._ternaryData_ = out._ternaryData_
            })
        }
    }

    // ===============================
    // Styling (apply cached colors)
    // ===============================
    //@override
    out.updateStyle = function () {
        if (out.insetTemplates_) {
            executeForAllInsets(out.insetTemplates_, out.svgId_, styleRegions)
        }
        styleRegions(out)

        addMouseEventsToRegions(out, out.svg().selectAll(getRegionsSelector(out)))
        return out
    }

    function styleRegions(map) {
        if (!map.svg() || !map._ternaryColorById_) return

        const selector = getRegionsSelector(map)
        const regions = map.svg().selectAll(selector)

        // defined here so it closes over the correct map instance
        const regionsFillFunction = function (rg) {
            const id = rg.properties.id
            const c = map._ternaryColorById_.get(id)
            const cls = map._ternaryClassById_.get(id)
            select(this).attr('ecl', cls ?? null)
            if (c != null) return c
            // explicit no-data (e.g. value ':') gets the no-data fill; regions that aren't
            // part of the dataset at all are left unstyled so the CSS background fill shows
            if (map._ternaryNoDataIds_?.has(id)) return out.noDataFillStyle_
            return null
        }

        regions
            .style('pointer-events', 'none')
            .transition()
            .duration(out.transitionDuration())
            .style('fill', regionsFillFunction)
            .end()
            .then(() => {
                // Re-enable interaction after the transition
                regions.style('pointer-events', null)
                // Store the original color for each region
                regions.each(function () {
                    const sel = select(this)
                    sel.attr('fill___', sel.style('fill'))
                })
                // Set up mouse events
                addMouseEventsToRegions(map, regions)

                // update font color for grid cartograms (contrast)
                if (out.gridCartogram_) {
                    map.svg()
                        .selectAll('.em-grid-text')
                        .each(function () {
                            const cellColor = select(this.parentNode).style('fill')
                            select(this).attr('fill', getTextColorForBackground(cellColor))
                        })
                }
            })
            .catch((err) => {
                //console.error('Error applying transition to regions:', err)
            })

        if (map.nutsLevel_ === 'mixed') {
            styleMixedNUTS(map)
        }
    }

    // when mixing different NUTS levels (e.g. showing NUTS 1 and NUTS 2 data simultaneously)
    const styleMixedNUTS = function (map) {
        map.svg()
            .selectAll(getRegionsSelector(map))
            .each(function () {
                if (this.parentNode.classList.contains('em-cntrg')) return // Skip country regions
                const sel = select(this)
                const ecl = sel.attr('ecl')
                const lvl = sel.attr('lvl')

                // Show level 0 as base layer and display upper levels only where data exists.
                const isVisible = ecl || lvl === '0'

                sel.style('display', isVisible ? 'block' : 'none')

                if (ecl && lvl !== '0') {
                    const stroke = sel.style('stroke') || '#777'
                    const strokeWidth = sel.style('stroke-width') || 0.2
                    sel.style('stroke', stroke).style('stroke-width', strokeWidth)
                }
            })
    }

    // ===============================
    // Hover / tooltip
    // ===============================
    function addMouseEventsToRegions(map, regions) {
        const shouldOmit = (id) => map.tooltip_.omitRegions?.includes(id)

        regions
            .on('mouseover', function (e, rg) {
                if (shouldOmit(rg.properties.id)) return
                const sel = select(this)
                sel.style('fill', map.hoverColor_)
                if (out.map._tooltip) out.map._tooltip.mouseover(out.tooltip_.textFunction(rg, out))
                if (out.onRegionMouseOver_) out.onRegionMouseOver_(e, rg, this, map)
            })
            .on('mousemove', function (e, rg) {
                if (shouldOmit(rg.properties.id)) return
                if (out.map._tooltip) out.map._tooltip.mousemove(e)
                if (out.onRegionMouseMove_) out.onRegionMouseMove_(e, rg, this, map)
            })
            .on('mouseout', function (e, rg) {
                if (shouldOmit(rg.properties.id)) return
                const sel = select(this)
                sel.style('fill', sel.attr('fill___'))
                if (out.map._tooltip) out.map._tooltip.mouseout()
                if (out.onRegionMouseOut_) out.onRegionMouseOut_(e, rg, this, map)
            })
    }

    //@override
    out.getLegendConstructor = function () {
        return TrivariateLegend.legend
    }

    return out
}

registerLayerType('trivariateChoropleth', 'base', decorateTrivariateChoroplethLayer)
registerLayerType('ternary', 'base', decorateTrivariateChoroplethLayer)
registerLayerType('chtri', 'base', decorateTrivariateChoroplethLayer)

// ===============================
// Tooltip
// ===============================
const tooltipTextFunctionTrivariate = function (rg, map) {
    const buf = []
    const name = rg.properties.na || ''
    const id = rg.properties.id || ''
    buf.push(`<div class="em-tooltip-bar">${name}${id ? ` (${id})` : ''}</div>`)

    const [c1, c2, c3] = map.getTernaryStatCodes?.() || map.ternaryCodes_

    const sv1 = map.statData(c1).get(id)
    const sv2 = map.statData(c2).get(id)
    const sv3 = map.statData(c3).get(id)

    const fmt = (sv) => (sv?.value != null && sv.value !== ':' ? spaceAsThousandSeparator(sv.value) + unitText : '')

    //we assume all three stats have the same unit
    const unitText = map.statData(c1).unitText() || map.statData(c2).unit_ || map.statData(c3).unit_ || ''

    buf.push(`<div class="em-tooltip-text" style="background:#fff;color:#171a22;padding:4px;font-size:15px;">
  <table class="em-tooltip-table"><tbody>
    <tr><td>${map.statData(c1).label_ || c1}: ${fmt(sv1)}</td></tr>
    <tr><td>${map.statData(c2).label_ || c2}: ${fmt(sv2)}</td></tr>
    <tr><td>${map.statData(c3).label_ || c3}: ${fmt(sv3)}</td></tr>
  </tbody></table></div>`)

    return buf.join('')
}
